import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import GraphqlProvider from '@sorare/core/src/contexts/graphql/Provider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCardsOnSale } from '@sorare/core/src/hooks/search/useCardsOnSale';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { Token } from '@sorare/marketplace/src/components/token/Token';
import MarketplaceProvider from '@sorare/marketplace/src/contexts/Marketplace/Provider';
import { TOKENS_BY_IDS_QUERY } from '@sorare/marketplace/src/lib/fragments';

interface Props {
  sport: Sport;
  rarity: Rarity;
  playerSlug: string;
}

const Row = styled(Horizontal)`
  padding: 50px 0px;
  justify-content: end;

  & > * {
    max-width: var(--card-width);
  }
`;

const CardsOnSale = ({ sport, rarity, playerSlug }: Props) => {
  const { slugs } = useCardsOnSale({
    sport,
    rarities: [rarity],
    playerSlugs: [playerSlug],
    limit: 5,
  });
  const { isAndroidApp, isIosApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const { data } = useQuery(TOKENS_BY_IDS_QUERY, {
    variables: {
      slugs,
      onlyPrimary: false,
      isAndroidApp,
      googlePlayStoreCountryCode,
      isIosApp,
    },
    skip: !slugs?.length,
  });
  return (
    <Row>
      {(data?.anyCards || []).map(card => (
        <Token key={card.slug} card={card} onlyPrimary={false} />
      ))}
    </Row>
  );
};

const CardsOnSaleBlock = ({ data }: { data: string }) => {
  const { locale } = useIntl();
  try {
    const attrs = JSON.parse(
      data
        .replace(
          /<data-sorare-cards-on-sale.*?>(.+?)<\/data-sorare-cards-on-sale>/,
          (_, json) => json
        )
        .replaceAll('&#8220;', '"')
        .replaceAll('&#8221;', '"')
        .replaceAll('&#8243;', '"')
    );
    return (
      <GraphqlProvider locale={locale}>
        <MarketplaceProvider>
          <CardsOnSale
            sport={attrs.sport as Sport}
            rarity={attrs.rarity as Rarity}
            playerSlug={attrs.playerSlug}
          />
        </MarketplaceProvider>
      </GraphqlProvider>
    );
  } catch {
    return null;
  }
};

export default CardsOnSaleBlock;
